import { computed, signal, WritableSignal } from '@angular/core';

export type CarouselDirection = -1 | 0 | 1;

export interface CarouselController<T> {
  // state
  indexSig: WritableSignal<number>;
  activeIndex: ReturnType<typeof computed<number>>;
  activeItem: ReturnType<typeof computed<T | null>>;
  prevItem: ReturnType<typeof computed<T | null>>;
  nextItem: ReturnType<typeof computed<T | null>>;

  // animation style bindings
  trackTransform: ReturnType<typeof computed<string>>;
  trackTransition: ReturnType<typeof computed<string>>;

  // controls
  prev(): void;
  next(): void;
  goTo(idx: number): void;

  // transition hook
  onTrackTransitionEnd(e: TransitionEvent): void;

  // swipe hooks
  onPointerDown(e: PointerEvent): void;
  onPointerMove(e: PointerEvent): void;
  onPointerUp(e: PointerEvent): void;
}

/**
 * 3-panel carousel controller (prev/current/next)
 * - animates the track to 0% or -200%
 * - on transitionend: commits index + snaps back to center with transition disabled
 */
export function createCarouselController<T>(opts: {
  items: () => T[];
  isInteractiveTarget?: (el: EventTarget | null) => boolean; // ignore swipe when moving on buttons/links
  swipeThreshold?: number;
  lockAngleRatio?: number;
  transitionMs?: number;
}): CarouselController<T> {
  const swipeThreshold = opts.swipeThreshold ?? 35;
  const lockAngleRatio = opts.lockAngleRatio ?? 1.2;
  const transitionMs = opts.transitionMs ?? 220;

  const indexSig = signal(0);

  const animating = signal(false);
  const direction = signal<CarouselDirection>(0);
  const transitionEnabled = signal(true);

  // swipe state
  let pointerActive = false;
  let startX = 0;
  let startY = 0;
  let hasSwiped = false;

  const activeIndex = computed(() => {
    const len = opts.items().length;
    if (len <= 0) return 0;
    const i = indexSig();
    return Math.max(0, Math.min(i, len - 1));
  });

  const activeItem = computed<T | null>(() => {
    const items = opts.items();
    return items[activeIndex()] ?? null;
  });

  const prevItem = computed<T | null>(() => {
    const items = opts.items();
    const len = items.length;
    if (!len) return null;
    return items[(activeIndex() - 1 + len) % len] ?? null;
  });

  const nextItem = computed<T | null>(() => {
    const items = opts.items();
    const len = items.length;
    if (!len) return null;
    return items[(activeIndex() + 1) % len] ?? null;
  });

  const trackTransition = computed(() =>
    transitionEnabled() ? `transform ${transitionMs}ms ease` : 'none'
  );

  const trackTransform = computed(() => {
    // center is -100%
    if (!animating()) return 'translateX(-100%)';

    const dir = direction();
    if (dir === 1) return 'translateX(-200%)';
    if (dir === -1) return 'translateX(0%)';
    return 'translateX(-100%)';
  });

  function prev() {
    const len = opts.items().length;
    if (len <= 1 || animating()) return;
    direction.set(-1);
    animating.set(true);
  }

  function next() {
    const len = opts.items().length;
    if (len <= 1 || animating()) return;
    direction.set(1);
    animating.set(true);
  }

  function goTo(idx: number) {
    // direct jump: no animation
    indexSig.set(idx);
  }

  function onTrackTransitionEnd(e: TransitionEvent) {
    if (e.propertyName !== 'transform') return;
    if (!animating()) return;

    const items = opts.items();
    const len = items.length;
    if (len <= 1) {
      animating.set(false);
      direction.set(0);
      return;
    }

    const dir = direction();
    const current = activeIndex();
    const nextIdx =
      dir === 1 ? (current + 1) % len : (current - 1 + len) % len;

    // snap reset pattern
    transitionEnabled.set(false);
    indexSig.set(nextIdx);
    animating.set(false);
    direction.set(0);

    requestAnimationFrame(() => transitionEnabled.set(true));
  }

  function onPointerDown(e: PointerEvent) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    pointerActive = true;
    hasSwiped = false;
    startX = e.clientX;
    startY = e.clientY;

    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: PointerEvent) {
    if (!pointerActive || hasSwiped) return;

    if (opts.isInteractiveTarget?.(e.target)) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (Math.abs(dy) > Math.abs(dx) * lockAngleRatio) return;

    if (Math.abs(dx) >= swipeThreshold) {
      hasSwiped = true;
      dx < 0 ? next() : prev();
    }
  }

  function onPointerUp(_: PointerEvent) {
    pointerActive = false;
    hasSwiped = false;
  }

  return {
    indexSig,
    activeIndex,
    activeItem,
    prevItem,
    nextItem,
    trackTransform,
    trackTransition,
    prev,
    next,
    goTo,
    onTrackTransitionEnd,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
